import { buttonStyle } from 'components/buttons/buttonStyle';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import { ChevronDownIcon, GlobeIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from 'components/ui/popover';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { NEW_FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSetBackOfficeSettingScopeMutation } from 'store/api/back-office-settings';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import type { SettingListElement, SettingScope } from 'types/back-office-settings';
import type { EmployeeListItemMinDto } from 'types/employees';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { getInitials } from 'utils/nameOrder';
import { SettingCaution } from './SettingCaution';
import { useSettingSave } from './useSettingSave';

/** `employees` holds what the people picker selects; a stored allow-list carries only each colleague's id and name. */
interface ScopeForm {
    scope: SettingScope;
    employees: EmployeeListItemMinDto[];
}

/** The chip that opens an audience popover; its size and gaps are the caller's. */
export const AUDIENCE_TRIGGER_CLASS =
    'border-divider new:border-border-control bg-paper text-grey-a400 new:text-foreground-secondary inline-flex items-center rounded-md border font-medium whitespace-nowrap disabled:cursor-default';

/** One of the two choices: the picked one is drawn in the brand green, as the saved view's "Who can see it" cards. */
export const choiceClass = (picked: boolean) =>
    cn(
        'flex gap-2.5 rounded-md border-[1.5px] px-3 py-2.5',
        picked ? 'border-primary bg-grey-50 new:bg-primary-wash' : 'border-divider new:border-border-menu'
    );

const storedScope = (setting: SettingListElement): ScopeForm => ({
    scope: setting.scope,
    employees: setting.allowedEmployees.map(({ id, fullName }) => ({ id, name: fullName }) as EmployeeListItemMinDto),
});

interface Props {
    setting: SettingListElement;
    readOnly: boolean;
}

/**
 * The "Who has it" button of an on setting and the popover it opens: the whole firm, or the colleagues picked. Cancel
 * drops the choice; Save writes the scope and the allow-list in one request.
 */
export function WhoHasItPopover({ setting, readOnly }: Props) {
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const [setScope, { isLoading: isSaving }] = useSetBackOfficeSettingScopeMutation();
    const save = useSettingSave();
    const { control, setValue, reset, getValues } = useForm<ScopeForm>({ defaultValues: storedScope(setting) });
    const scope = useWatch({ control, name: 'scope' });
    const employees = useWatch({ control, name: 'employees' });

    const people = setting.allowedEmployees;
    const isFirm = setting.scope === 'TENANT';
    const colleagues = (count: number) => intl.formatMessage({ id: 'back-office-settings.colleagues' }, { count });

    const onOpenChange = (next: boolean) => {
        if (next) reset(storedScope(setting));
        setOpen(next);
    };

    const onSave = async () => {
        const { scope: chosen, employees: picked } = getValues();
        const saved = await save(() =>
            setScope({ key: setting.key, scope: chosen, employeeIds: picked.map(({ id }) => id) }).unwrap()
        );
        if (saved) setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger
                disabled={readOnly}
                aria-label={
                    isFirm
                        ? t('back-office-settings.who-has-it-firm')
                        : intl.formatMessage(
                              { id: 'back-office-settings.who-has-it-colleagues' },
                              { count: people.length }
                          )
                }
                className={cn(AUDIENCE_TRIGGER_CLASS, 'h-7 gap-1.5 pr-2 pl-2.5 text-[13px]', NEW_FOCUS_RING)}
            >
                {isFirm ? (
                    <>
                        <GlobeIcon className="size-3.5" />
                        <FormattedMessage id="back-office-settings.whole-firm" />
                    </>
                ) : (
                    <>
                        <span className="flex pl-1.5" aria-hidden>
                            {people.slice(0, 3).map(({ id, fullName }) => (
                                <span
                                    key={id}
                                    className="border-paper bg-grey-200 text-grey-a400 new:bg-primary-tint new:text-primary-strong new:text-caption new:size-6 -ml-1.5 inline-flex size-5 items-center justify-center rounded-full border-[1.5px] text-[9.5px] font-semibold"
                                >
                                    {getInitials(fullName)}
                                </span>
                            ))}
                        </span>
                        {colleagues(people.length)}
                    </>
                )}
                <ChevronDownIcon className="size-3.5" />
            </PopoverTrigger>
            <PopoverContent
                align="end"
                sideOffset={8}
                className="new:ring-border-menu ring-divider w-[380px] gap-3 rounded-[10px] text-[13px]"
            >
                <PopoverTitle className="text-fg text-sm font-semibold">
                    <FormattedMessage id="back-office-settings.who-has-it" />
                </PopoverTitle>
                <RadioGroup
                    value={scope}
                    onValueChange={(value) => setValue('scope', value as SettingScope)}
                    aria-label={t('back-office-settings.who-has-it')}
                    className="gap-3"
                >
                    <label className={cn(choiceClass(scope === 'TENANT'), 'cursor-pointer items-start')}>
                        <RadioGroupItem variant="primary" value="TENANT" className="mt-px" />
                        <ChoiceText
                            titleId="back-office-settings.whole-firm"
                            descriptionId="back-office-settings.whole-firm-description"
                        />
                    </label>
                    <div className={cn(choiceClass(scope === 'EMPLOYEE'), 'flex-col')}>
                        <label className="flex cursor-pointer items-start gap-2.5">
                            <RadioGroupItem variant="primary" value="EMPLOYEE" className="mt-px" />
                            <ChoiceText
                                titleId="back-office-settings.selected-colleagues"
                                descriptionId="back-office-settings.selected-colleagues-description"
                            />
                        </label>
                        {scope === 'EMPLOYEE' ? (
                            <>
                                <DynamicMultiSelectInput<ScopeForm, EmployeeListItemMinDto>
                                    control={control}
                                    setValue={setValue}
                                    name="employees"
                                    placeholder="back-office-settings.add-colleague"
                                    paginated={{
                                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                                        searchField: 'fullName',
                                    }}
                                    getOptionLabel="name"
                                    getOptionIdentifier="id"
                                />
                                <p className="text-grey-600 m-0 text-xs">
                                    <FormattedMessage
                                        id="back-office-settings.colleagues-have-it"
                                        values={{ count: employees.length }}
                                    />
                                </p>
                            </>
                        ) : null}
                    </div>
                </RadioGroup>
                {setting.firmOnlyWarning && scope === 'EMPLOYEE' ? (
                    <SettingCaution text={t(`back-office-settings.${setting.key}.firm-only`)} />
                ) : null}
                <p className="text-grey-600 m-0 text-xs leading-[17px]">
                    <FormattedMessage id="back-office-settings.no-colleague-note" />
                </p>
                <div className="flex justify-end gap-2">
                    <Button {...buttonStyle({ variant: 'outlined' }, 'h-8 px-3')} onClick={() => setOpen(false)}>
                        <FormattedMessage id="cancel" />
                    </Button>
                    <Button
                        {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'h-8 px-3.5')}
                        disabled={isSaving || (scope === 'EMPLOYEE' && employees.length === 0)}
                        onClick={() => void onSave()}
                    >
                        <FormattedMessage id="save" />
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

export const ChoiceText = ({ titleId, descriptionId }: { titleId: string; descriptionId: string }) => (
    <span className="flex flex-col gap-0.5">
        <span className="text-fg font-semibold">
            <FormattedMessage id={titleId} />
        </span>
        <span className="text-grey-600 text-xs">
            <FormattedMessage id={descriptionId} />
        </span>
    </span>
);
